import { useEffect, useState, useCallback } from 'react';
import { Camera, ChevronLeft, ChevronRight, X, Download, Share2, Facebook, Instagram, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveMediaUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}${url}`;
  return url;
};

const Gallery = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [loadingFullImage, setLoadingFullImage] = useState(false);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API}/galleries`);
      setGalleries(response.data);
    } catch (error) {
      console.error('Error fetching galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  const openGallery = async (gallery) => {
    setSelectedGallery(gallery);
    setLoadingPhotos(true);
    try {
      const response = await axios.get(`${API}/galleries/${gallery.id}/photos?thumbnails_only=true`);
      setGalleryPhotos(response.data);
    } catch (error) {
      console.error('Error fetching gallery photos:', error);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const closeGallery = () => {
    setSelectedGallery(null);
    setGalleryPhotos([]);
  };

  const openLightbox = async (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
    await loadFullImage(galleryPhotos[index].id);
  };

  const loadFullImage = useCallback(async (photoId) => {
    setLoadingFullImage(true);
    try {
      const response = await axios.get(`${API}/galleries/${selectedGallery.id}/photos/${photoId}`);
      setCurrentPhoto(response.data);
    } catch (error) {
      console.error('Error loading full image:', error);
    } finally {
      setLoadingFullImage(false);
    }
  }, [selectedGallery]);

  const navigatePhoto = useCallback(async (direction) => {
    const newIndex = direction === 'next' 
      ? (currentPhotoIndex + 1) % galleryPhotos.length
      : (currentPhotoIndex - 1 + galleryPhotos.length) % galleryPhotos.length;
    
    setCurrentPhotoIndex(newIndex);
    await loadFullImage(galleryPhotos[newIndex].id);
  }, [currentPhotoIndex, galleryPhotos, selectedGallery, loadFullImage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowRight') navigatePhoto('next');
      if (e.key === 'ArrowLeft') navigatePhoto('prev');
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, navigatePhoto]);

  const downloadImage = () => {
    if (!currentPhoto) return;
    
    const link = document.createElement('a');
    link.href = resolveMediaUrl(currentPhoto.original_url);
    link.download = currentPhoto.filename || 'photo.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnInstagram = () => {
    // Instagram doesn't have a direct share URL, so we copy to clipboard
    if (currentPhoto) {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copiado! Cole no Instagram para compartilhar.');
    }
  };

  // Gallery list view
  if (!selectedGallery) {
    return (
      <div data-testid="gallery-page" className="min-h-screen py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="mb-12">
          <h1 className="text-4xl md:text-6xl font-heading font-black tracking-tight uppercase text-primary mb-4">
            Galeria
          </h1>
          <p className="text-base md:text-lg text-foreground/70 max-w-2xl">
            Reviva os melhores momentos das nossas competições através de fotos profissionais.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[4/3] bg-secondary/50 animate-pulse rounded-lg"></div>
            ))}
          </div>
        ) : galleries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleries.map((gallery) => (
              <div
                key={gallery.id}
                data-testid={`gallery-card-${gallery.id}`}
                onClick={() => openGallery(gallery)}
                className="group relative aspect-[4/3] overflow-hidden rounded-lg cursor-pointer bg-secondary"
              >
                {gallery.cover_photo ? (
                  <img
                    src={resolveMediaUrl(gallery.cover_photo.thumbnail_url)}
                    alt={gallery.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-secondary">
                    <Camera className="w-16 h-16 text-foreground/20" />
                  </div>
                )}
                
                {/* Title overlay - always visible */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-white font-heading font-bold uppercase text-xl mb-1">
                      {gallery.event?.name || gallery.title}
                    </h3>
                    <p className="text-white/70 text-sm">
                      {gallery.photo_count} {gallery.photo_count === 1 ? 'foto' : 'fotos'}
                    </p>
                    {gallery.event?.date && (
                      <p className="text-white/50 text-xs mt-1">
                        {new Date(gallery.event.date).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-24 bg-card border border-border rounded-lg">
            <Camera className="w-16 h-16 text-foreground/30 mx-auto mb-4" />
            <p className="text-xl text-foreground/50">Nenhuma galeria disponível ainda.</p>
          </div>
        )}
      </div>
    );
  }

  // Gallery detail view (mosaic)
  return (
    <div data-testid="gallery-detail" className="min-h-screen py-24 md:py-32 px-6 md:px-12 lg:px-24">
      {/* Back button and title */}
      <div className="mb-8">
        <Button
          data-testid="back-to-galleries"
          onClick={closeGallery}
          variant="ghost"
          className="mb-4 hover:bg-secondary"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Voltar para Galerias
        </Button>
        
        <h1 className="text-3xl md:text-5xl font-heading font-black tracking-tight uppercase text-primary mb-2">
          {selectedGallery.event?.name || selectedGallery.title}
        </h1>
        {selectedGallery.description && (
          <p className="text-foreground/70">{selectedGallery.description}</p>
        )}
        <p className="text-sm text-foreground/50 mt-2">
          {galleryPhotos.length} {galleryPhotos.length === 1 ? 'foto' : 'fotos'}
        </p>
      </div>

      {/* Photo mosaic */}
      {loadingPhotos ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : galleryPhotos.length > 0 ? (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {galleryPhotos.map((photo, index) => (
            <div
              key={photo.id}
              data-testid={`gallery-photo-${photo.id}`}
              onClick={() => openLightbox(index)}
              className="break-inside-avoid cursor-pointer group relative overflow-hidden rounded-lg"
            >
              <img
                src={resolveMediaUrl(photo.thumbnail_url || photo.url)}
                alt={photo.filename}
                className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-24 bg-card border border-border rounded-lg">
          <Camera className="w-16 h-16 text-foreground/30 mx-auto mb-4" />
          <p className="text-xl text-foreground/50">Esta galeria ainda não possui fotos.</p>
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent 
          data-testid="photo-lightbox"
          className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-0"
        >
          <div className="relative w-full h-[90vh] flex items-center justify-center">
            {/* Close button */}
            <button
              data-testid="lightbox-close"
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Navigation buttons */}
            <button
              data-testid="lightbox-prev"
              onClick={() => navigatePhoto('prev')}
              className="absolute left-4 z-50 p-3 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>

            <button
              data-testid="lightbox-next"
              onClick={() => navigatePhoto('next')}
              className="absolute right-4 z-50 p-3 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>

            {/* Image */}
            <div className="relative max-w-full max-h-full flex items-center justify-center p-4">
              {loadingFullImage ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="w-12 h-12 animate-spin text-white" />
                </div>
              ) : currentPhoto ? (
                <img
                  src={resolveMediaUrl(currentPhoto.original_url)}
                  alt={currentPhoto.filename}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : null}
            </div>

            {/* Bottom toolbar */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4 bg-black/70 px-6 py-3 rounded-full">
              {/* Photo counter */}
              <span className="text-white text-sm">
                {currentPhotoIndex + 1} / {galleryPhotos.length}
              </span>

              <div className="w-px h-6 bg-white/30" />

              {/* Download button */}
              <button
                data-testid="lightbox-download"
                onClick={downloadImage}
                className="p-2 rounded-full hover:bg-white/20 transition-colors"
                title="Download"
              >
                <Download className="w-5 h-5 text-white" />
              </button>

              {/* Share buttons */}
              <button
                data-testid="lightbox-share-facebook"
                onClick={shareOnFacebook}
                className="p-2 rounded-full hover:bg-white/20 transition-colors"
                title="Compartilhar no Facebook"
              >
                <Facebook className="w-5 h-5 text-white" />
              </button>

              <button
                data-testid="lightbox-share-instagram"
                onClick={shareOnInstagram}
                className="p-2 rounded-full hover:bg-white/20 transition-colors"
                title="Compartilhar no Instagram"
              >
                <Instagram className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
